import React, { useState, useEffect } from 'react';
import api from '../services/api';

const TABS = ['AI Analysis', 'Automation Rules', 'Alerts', 'History'];

const DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const severityColor = { INFO: '#059669', WARNING: '#d97706', CRITICAL: '#dc2626' };
const severityBg = { INFO: '#d1fae5', WARNING: '#fef3c7', CRITICAL: '#fee2e2' };

const CAP_SUFFIXES = ['-approuter', '-srv', '-html5-deployer', '-db-deployer', '-mtx-sidecar'];

const emptyRule = {
  projectId: '', ruleType: 'SCHEDULE_STOP', scheduleTime: '20:00', scheduleDays: 'MON,TUE,WED,THU,FRI',
  maxInstances: 5, minInstances: 1, memoryThresholdPct: 80, capAppSuffixes: '', active: true
};

export default function AiAnalysis() {
  const [tab, setTab] = useState(0);
  const [projects, setProjects] = useState([]);
  const [rules, setRules] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [analysis, setAnalysis] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [ruleForm, setRuleForm] = useState({ ...emptyRule });
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [filterProject, setFilterProject] = useState('');

  // History tab state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [histFilter, setHistFilter] = useState({ projectId: '', ruleId: '', alertType: '' });
  const [expandedLog, setExpandedLog] = useState(null);
  const [historyError, setHistoryError] = useState('');

  const load = async () => {
    const [p, r, a] = await Promise.all([
      api.get('/projects'),
      api.get('/ai/rules'),
      api.get('/ai/alerts'),
    ]);
    setProjects(p.data);
    setRules(r.data);
    setAlerts(a.data);
  };
  useEffect(() => { load(); }, []);

  const loadHistory = async (filters = histFilter) => {
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const params = new URLSearchParams();
      if (filters.projectId) params.set('projectId', filters.projectId);
      if (filters.ruleId) params.set('ruleId', filters.ruleId);
      if (filters.alertType) params.set('alertType', filters.alertType);
      const { data } = await api.get(`/ai/history?${params}`);
      setHistory(data);
    } catch (e) {
      setHistoryError(`Failed to load history: ${e.response?.status} ${e.response?.data?.message || e.message}`);
    }
    setHistoryLoading(false);
  };

  // History loads manually via button — not on tab click

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (histFilter.projectId) params.set('projectId', histFilter.projectId);
    if (histFilter.ruleId) params.set('ruleId', histFilter.ruleId);
    if (histFilter.alertType) params.set('alertType', histFilter.alertType);
    const token = localStorage.getItem('token');
    fetch(`/api/ai/history/export?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'automation-history.csv'; a.click();
        URL.revokeObjectURL(url);
      });
  };

  const analyze = async () => {
    if (!selectedProject) return;
    setAnalyzing(true);
    setAnalysis('');
    try {
      const { data } = await api.post(`/ai/analyze/${selectedProject}`);
      setAnalysis(data.analysis);
    } catch (e) {
      setAnalysis('Error: ' + (e.response?.data?.error || e.message));
    }
    setAnalyzing(false);
  };

  const saveRule = async () => {
    if (!ruleForm.projectId) return alert('Select a project');
    try {
      const payload = { ...ruleForm, projectId: Number(ruleForm.projectId) };
      if (editingRuleId) {
        await api.put(`/ai/rules/${editingRuleId}`, payload);
      } else {
        await api.post('/ai/rules', payload);
      }
      setShowRuleForm(false);
      setEditingRuleId(null);
      setRuleForm({ ...emptyRule });
      await load();
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.message || e.message));
    }
  };

  const editRule = (rule) => {
    setRuleForm({
      projectId: String(rule.projectId),
      ruleType: rule.ruleType,
      scheduleTime: rule.scheduleTime || '20:00',
      scheduleDays: rule.scheduleDays || 'MON,TUE,WED,THU,FRI',
      maxInstances: rule.maxInstances || 5,
      minInstances: rule.minInstances || 1,
      memoryThresholdPct: rule.memoryThresholdPct || 80,
      capAppSuffixes: rule.capAppSuffixes || '',
      active: rule.active,
    });
    setEditingRuleId(rule.id);
    setShowRuleForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleRule = async (rule) => {
    await api.put(`/ai/rules/${rule.id}`, { ...rule, active: !rule.active });
    load();
  };

  const deleteRule = async (id) => {
    if (window.confirm('Delete this rule?')) { await api.delete(`/ai/rules/${id}`); load(); }
  };

  const deleteAlert = async (id) => {
    await api.delete(`/ai/alerts/${id}`); load();
  };

  const projectName = (id) => projects.find(p => p.id === Number(id))?.name || `#${id}`;
  const filteredAlerts = filterProject ? alerts.filter(a => a.projectId === Number(filterProject)) : alerts;

  const formatTime12h = (time24) => {
    if (!time24) return time24;
    const [h, m] = time24.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  const ruleLabel = (r) => {
    const t = formatTime12h(r.scheduleTime);
    if (r.ruleType === 'SCHEDULE_STOP') return `Stop at ${t} on ${r.scheduleDays}`;
    if (r.ruleType === 'SCHEDULE_START') return `Start at ${t} on ${r.scheduleDays}`;
    if (r.ruleType === 'AUTO_SCALE') return `Auto-scale ${r.minInstances}–${r.maxInstances} instances (mem >${r.memoryThresholdPct}%)`;
    if (r.ruleType === 'HEALTH_RESTART') return `Auto-restart on crash`;
    return r.ruleType;
  };

  const ruleIcon = (t) => ({ SCHEDULE_STOP: '🛑', SCHEDULE_START: '▶️', AUTO_SCALE: '📈', HEALTH_RESTART: '🔄' }[t] || '⚙️');

  const renderMarkdown = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      if (line.startsWith('## ')) return <h3 key={i} style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: '16px 0 6px' }}>{line.replace('## ', '')}</h3>;
      if (line.startsWith('- ')) return <div key={i} style={{ paddingLeft: 16, marginBottom: 4, color: '#374151', fontSize: 14 }}>• {line.replace('- ', '')}</div>;
      if (line.trim() === '') return <div key={i} style={{ height: 6 }} />;
      return <div key={i} style={{ color: '#374151', fontSize: 14, marginBottom: 2 }}>{line}</div>;
    });
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>AI Analysis & Automation</h1>
        <p style={{ color: '#64748b', fontSize: 14 }}>Powered by OpenAI GPT — analyze apps, schedule actions, auto-scale</p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid #e2e8f0' }}>
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)} style={{
            padding: '10px 20px', fontSize: 14, fontWeight: 500, border: 'none', cursor: 'pointer',
            background: 'none', borderBottom: tab === i ? '2px solid #1a56db' : '2px solid transparent',
            color: tab === i ? '#1a56db' : '#6b7280', marginBottom: -1
          }}>{t}</button>
        ))}
      </div>

      {/* Tab 0: AI Analysis */}
      {tab === 0 && (
        <div>
          <div className="card" style={{ marginBottom: 20 }}>
            <h3 style={{ fontWeight: 600, marginBottom: 16 }}>Analyze Application Health</h3>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Select CF Project</label>
                <select value={selectedProject} onChange={e => { setSelectedProject(e.target.value); setAnalysis(''); }}
                  style={{ width: '100%' }}>
                  <option value="">Choose a project...</option>
                  {projects.filter(p => p.platform === 'CF' && p.cfAppName).map(p => (
                    <option key={p.id} value={p.id}>{p.name} — {p.cfAppName}</option>
                  ))}
                </select>
              </div>
              <button className="btn-primary" onClick={analyze} disabled={!selectedProject || analyzing}
                style={{ padding: '10px 24px', whiteSpace: 'nowrap' }}>
                {analyzing ? '🤖 Analyzing...' : '🤖 Analyze with AI'}
              </button>
            </div>

            {analyzing && (
              <div style={{ marginTop: 20, padding: 20, background: '#f8fafc', borderRadius: 8, textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🤖</div>
                <div style={{ color: '#6b7280', fontSize: 14 }}>Claude is fetching CF status and logs, then analyzing...</div>
                <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>This may take 15–30 seconds</div>
              </div>
            )}

            {analysis && !analyzing && (
              <div style={{ marginTop: 20, padding: 20, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <span style={{ fontSize: 20 }}>🤖</span>
                  <span style={{ fontWeight: 600, color: '#0f172a' }}>GPT AI Analysis</span>
                  <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 'auto' }}>
                    {projects.find(p => p.id === Number(selectedProject))?.name}
                  </span>
                </div>
                <div>{renderMarkdown(analysis)}</div>
              </div>
            )}
          </div>

          {/* Recent AI Alerts for this project */}
          {selectedProject && (
            <div className="card">
              <h3 style={{ fontWeight: 600, marginBottom: 16 }}>Previous Analyses</h3>
              {alerts.filter(a => a.projectId === Number(selectedProject) && a.alertType === 'AI_ANALYSIS').length === 0
                ? <p style={{ color: '#9ca3af', fontSize: 14 }}>No previous analyses</p>
                : alerts.filter(a => a.projectId === Number(selectedProject) && a.alertType === 'AI_ANALYSIS').slice(0, 5).map(a => (
                  <div key={a.id} style={{ padding: 12, borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0', marginBottom: 8 }}>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>{new Date(a.createdAt).toLocaleString()}</div>
                    <div style={{ fontSize: 13, color: '#374151' }}>{a.message?.split('\n').slice(0, 3).join(' ')}</div>
                  </div>
                ))
              }
            </div>
          )}
        </div>
      )}

      {/* Tab 1: Automation Rules */}
      {tab === 1 && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button className="btn-primary" onClick={() => setShowRuleForm(!showRuleForm)}>+ Add Rule</button>
          </div>

          {showRuleForm && (
            <div className="card" style={{ marginBottom: 20 }}>
              <h3 style={{ fontWeight: 600, marginBottom: 16 }}>{editingRuleId ? 'Edit Automation Rule' : 'New Automation Rule'}</h3>
              {editingRuleId && (
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 12 }}>
                  Rule #{editingRuleId} · CAP suffixes saved: <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 3 }}>{ruleForm.capAppSuffixes || '(none)'}</code>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Project</label>
                  <select value={ruleForm.projectId} onChange={e => setRuleForm({ ...ruleForm, projectId: e.target.value })}>
                    <option value="">Select project</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name} {p.cfAppName ? `(${p.cfAppName})` : ''}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Rule Type</label>
                  <select value={ruleForm.ruleType} onChange={e => setRuleForm({ ...ruleForm, ruleType: e.target.value })}>
                    <option value="SCHEDULE_STOP">🛑 Scheduled Stop</option>
                    <option value="SCHEDULE_START">▶️ Scheduled Start</option>
                    <option value="AUTO_SCALE">📈 Auto Scale</option>
                    <option value="HEALTH_RESTART">🔄 Auto Restart on Crash</option>
                  </select>
                </div>

                {(ruleForm.ruleType === 'SCHEDULE_STOP' || ruleForm.ruleType === 'SCHEDULE_START') && (<>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Time (HH:mm)</label>
                    <input type="time" value={ruleForm.scheduleTime} onChange={e => setRuleForm({ ...ruleForm, scheduleTime: e.target.value })} />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Days</label>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {DAYS.map(d => {
                        const selected = ruleForm.scheduleDays.split(',').includes(d);
                        return (
                          <button key={d} onClick={() => {
                            const days = ruleForm.scheduleDays.split(',').filter(Boolean);
                            const next = selected ? days.filter(x => x !== d) : [...days, d];
                            setRuleForm({ ...ruleForm, scheduleDays: next.join(',') || 'ALL' });
                          }} style={{
                            padding: '4px 10px', fontSize: 12, borderRadius: 6, cursor: 'pointer', border: 'none',
                            background: selected ? '#1a56db' : '#e2e8f0', color: selected ? '#fff' : '#374151', fontWeight: 500
                          }}>{d}</button>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{ marginBottom: 12, gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
                      SAP CAP App Components
                      <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8 }}>Select which services to stop/start (leave all unselected for single-app projects)</span>
                    </label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {CAP_SUFFIXES.map(suffix => {
                        const selected = ruleForm.capAppSuffixes.split(',').map(s => s.trim()).includes(suffix);
                        return (
                          <button key={suffix} type="button" onClick={() => {
                            const current = ruleForm.capAppSuffixes.split(',').map(s => s.trim()).filter(Boolean);
                            const next = selected ? current.filter(x => x !== suffix) : [...current, suffix];
                            setRuleForm({ ...ruleForm, capAppSuffixes: next.join(',') });
                          }} style={{
                            padding: '5px 12px', fontSize: 12, borderRadius: 6, cursor: 'pointer',
                            border: `1px solid ${selected ? '#1a56db' : '#e2e8f0'}`,
                            background: selected ? '#eff6ff' : '#f8fafc',
                            color: selected ? '#1a56db' : '#64748b', fontWeight: selected ? 600 : 400
                          }}>
                            {selected ? '✓ ' : ''}{suffix}
                          </button>
                        );
                      })}
                    </div>
                    {ruleForm.capAppSuffixes && (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
                        Will target: {ruleForm.capAppSuffixes.split(',').filter(Boolean).map(s =>
                          <code key={s} style={{ background: '#e0f2fe', color: '#0369a1', padding: '1px 6px', borderRadius: 4, marginRight: 4 }}>
                            {projects.find(p => p.id === Number(ruleForm.projectId))?.cfAppName || '<app>'}{s}
                          </code>
                        )}
                      </div>
                    )}
                  </div>
                </>)}

                {ruleForm.ruleType === 'AUTO_SCALE' && (<>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Min Instances</label>
                    <input type="number" min="1" value={ruleForm.minInstances} onChange={e => setRuleForm({ ...ruleForm, minInstances: Number(e.target.value) })} />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Max Instances</label>
                    <input type="number" min="1" value={ruleForm.maxInstances} onChange={e => setRuleForm({ ...ruleForm, maxInstances: Number(e.target.value) })} />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Scale-up Memory Threshold (%)</label>
                    <input type="number" min="1" max="99" value={ruleForm.memoryThresholdPct} onChange={e => setRuleForm({ ...ruleForm, memoryThresholdPct: Number(e.target.value) })} />
                  </div>
                </>)}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button className="btn-primary" onClick={saveRule}>{editingRuleId ? 'Update Rule' : 'Save Rule'}</button>
                <button className="btn-ghost" onClick={() => { setShowRuleForm(false); setEditingRuleId(null); setRuleForm({ ...emptyRule }); }}>Cancel</button>
              </div>
            </div>
          )}

          <div className="card">
            {rules.length === 0 ? <p style={{ color: '#9ca3af', fontSize: 14 }}>No automation rules. Add one to schedule stop/start or enable auto-scaling.</p> : (
              <table>
                <thead><tr><th>Project</th><th>Rule</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {rules.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontWeight: 500 }}>{projectName(r.projectId)}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span>{ruleIcon(r.ruleType)}</span>
                          <span>{ruleLabel(r)}</span>
                          {r.capAppSuffixes && r.capAppSuffixes.split(',').filter(Boolean).map(s => (
                            <span key={s} style={{
                              fontSize: 11, padding: '2px 7px', borderRadius: 4,
                              background: '#eff6ff', color: '#1a56db', border: '1px solid #bfdbfe', fontWeight: 500
                            }}>{s.trim()}</span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${r.active ? 'badge-success' : 'badge-gray'}`}>
                          {r.active ? 'Active' : 'Paused'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn-ghost" style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => editRule(r)}>Edit</button>
                          <button className="btn-ghost" style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => toggleRule(r)}>
                            {r.active ? 'Pause' : 'Enable'}
                          </button>
                          <button className="btn-danger" style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => deleteRule(r.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Tab 3: History */}
      {tab === 3 && (
        <div>
          {/* Filters + Export */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: '#64748b' }}>Project</label>
                <select value={histFilter.projectId} style={{ fontSize: 13, padding: '6px 10px', minWidth: 160 }}
                  onChange={e => setHistFilter(f => ({ ...f, projectId: e.target.value }))}>
                  <option value="">All Projects</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: '#64748b' }}>Rule</label>
                <select value={histFilter.ruleId} style={{ fontSize: 13, padding: '6px 10px', minWidth: 160 }}
                  onChange={e => setHistFilter(f => ({ ...f, ruleId: e.target.value }))}>
                  <option value="">All Rules</option>
                  {rules.map(r => <option key={r.id} value={r.id}>{ruleIcon(r.ruleType)} {projectName(r.projectId)} — {r.ruleType}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: '#64748b' }}>Type</label>
                <select value={histFilter.alertType} style={{ fontSize: 13, padding: '6px 10px' }}
                  onChange={e => setHistFilter(f => ({ ...f, alertType: e.target.value }))}>
                  <option value="">All Types</option>
                  <option value="YUVI_ACTION">🤖 Yuvi Action</option>
                  <option value="YUVI_CHAT">🤖 Yuvi Chat</option>
                  <option value="SCHEDULE">SCHEDULE</option>
                  <option value="AUTO_SCALE">AUTO_SCALE</option>
                  <option value="HEALTH_RESTART">HEALTH_RESTART</option>
                  <option value="AI_ANALYSIS">AI_ANALYSIS</option>
                </select>
              </div>
              <button className="btn-primary" style={{ padding: '7px 18px', fontSize: 13 }}
                onClick={() => loadHistory(histFilter)} disabled={historyLoading}>
                {historyLoading ? 'Loading...' : 'Search'}
              </button>
              <button className="btn-ghost" style={{ padding: '7px 14px', fontSize: 13 }}
                onClick={() => { const f = { projectId: '', ruleId: '', alertType: '' }; setHistFilter(f); loadHistory(f); }}>
                Clear
              </button>
              <div style={{ marginLeft: 'auto' }}>
                <button onClick={exportCsv} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', fontSize: 13,
                  background: '#059669', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 500
                }}>
                  ⬇ Export CSV
                </button>
              </div>
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: historyError ? '#dc2626' : '#94a3b8' }}>
              {historyLoading ? 'Loading...' : historyError ? historyError : `${history.length} log entries`}
            </div>
          </div>

          {/* Log Table */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {history.length === 0 && !historyLoading ? (
              <p style={{ color: '#9ca3af', fontSize: 14, padding: 20 }}>No history logs found. Automation rules log every execution here.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: '#64748b', fontWeight: 600 }}>Timestamp</th>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: '#64748b', fontWeight: 600 }}>Project</th>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: '#64748b', fontWeight: 600 }}>Type</th>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: '#64748b', fontWeight: 600 }}>Severity</th>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: '#64748b', fontWeight: 600 }}>Action</th>
                    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, color: '#64748b', fontWeight: 600 }}>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(h => (
                    <>
                      <tr key={h.id} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                        onClick={() => setExpandedLog(expandedLog === h.id ? null : h.id)}>
                        <td style={{ padding: '10px 16px', fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                          {new Date(h.createdAt).toLocaleString()}
                        </td>
                        <td style={{ padding: '10px 16px', fontSize: 13, fontWeight: 500 }}>{projectName(h.projectId)}</td>
                        <td style={{ padding: '10px 16px' }}>
                          {h.alertType === 'YUVI_ACTION' ? (
                            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#fef3c7', color: '#92400e', fontWeight: 600 }}>
                              🤖 Yuvi Action
                            </span>
                          ) : h.alertType === 'YUVI_CHAT' ? (
                            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#ede9fe', color: '#6d28d9', fontWeight: 600 }}>
                              🤖 Yuvi Chat
                            </span>
                          ) : (
                            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#e0f2fe', color: '#0369a1', fontWeight: 600 }}>
                              {h.alertType}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '10px 16px' }}>
                          <span className={`badge ${h.severity === 'CRITICAL' ? 'badge-danger' : h.severity === 'WARNING' ? 'badge-warning' : 'badge-success'}`}>
                            {h.severity}
                          </span>
                        </td>
                        <td style={{ padding: '10px 16px', fontSize: 12, color: '#374151', maxWidth: 200 }}>
                          {h.actionTaken && (
                            <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {h.alertType === 'YUVI_CHAT'
                                ? <em style={{ color: '#6d28d9', fontSize: 11 }}>{h.actionTaken?.substring(0, 80)}...</em>
                                : <code style={{ background: '#e2e8f0', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>{h.actionTaken}</code>
                              }
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '10px 16px', fontSize: 13, color: '#374151', maxWidth: 300 }}>
                          <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {h.message?.split('\n')[0]}
                          </span>
                          {(h.message?.includes('\n') || h.actionTaken?.length > 80) && (
                            <span style={{ fontSize: 11, color: '#1a56db', marginTop: 2 }}>
                              {expandedLog === h.id ? '▲ collapse' : '▼ expand'}
                            </span>
                          )}
                        </td>
                      </tr>
                      {expandedLog === h.id && (
                        <tr key={`${h.id}-exp`} style={{ background: '#f8fafc' }}>
                          <td colSpan={6} style={{ padding: '12px 16px' }}>
                            {(h.alertType === 'YUVI_CHAT' || h.alertType === 'YUVI_ACTION') && (
                              <div style={{ marginBottom: 8 }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: '#6d28d9', marginBottom: 4 }}>🤖 Yuvi's Response:</div>
                                <pre style={{ fontSize: 12, color: '#374151', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, maxHeight: 200, overflowY: 'auto', background: '#ede9fe', padding: 10, borderRadius: 6 }}>
                                  {h.actionTaken}
                                </pre>
                                <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', marginTop: 8, marginBottom: 4 }}>Question / Context:</div>
                              </div>
                            )}
                            <pre style={{ fontSize: 12, color: '#374151', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, maxHeight: 300, overflowY: 'auto' }}>
                              {h.message}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Tab 2: Alerts */}
      {tab === 2 && (
        <div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
            <select value={filterProject} onChange={e => setFilterProject(e.target.value)} style={{ width: 'auto', fontSize: 13, padding: '6px 10px' }}>
              <option value="">All Projects</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {filterProject && <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setFilterProject('')}>Clear</button>}
          </div>

          <div className="card">
            {filteredAlerts.length === 0 ? <p style={{ color: '#9ca3af', fontSize: 14 }}>No alerts yet. Alerts appear here after AI analyses and automation actions.</p> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {filteredAlerts.map(a => (
                  <div key={a.id} style={{
                    padding: '12px 16px', borderRadius: 8, border: '1px solid #e2e8f0',
                    borderLeft: `4px solid ${severityColor[a.severity] || '#94a3b8'}`,
                    background: severityBg[a.severity] || '#f8fafc'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                          <span className={`badge ${a.severity === 'CRITICAL' ? 'badge-danger' : a.severity === 'WARNING' ? 'badge-warning' : 'badge-success'}`}>
                            {a.severity}
                          </span>
                          <span className="badge badge-info">{a.alertType}</span>
                          <span style={{ fontSize: 12, color: '#94a3b8' }}>{projectName(a.projectId)}</span>
                          <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 'auto' }}>{new Date(a.createdAt).toLocaleString()}</span>
                        </div>
                        {a.alertType === 'AI_ANALYSIS'
                          ? <div style={{ fontSize: 13, color: '#374151' }}>{a.message?.split('\n').slice(0, 4).join('\n')}</div>
                          : <div style={{ fontSize: 13, color: '#374151' }}>{a.message}</div>
                        }
                        {a.actionTaken && a.alertType !== 'AI_ANALYSIS' && (
                          <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                            Action: <code style={{ background: '#e2e8f0', padding: '1px 6px', borderRadius: 4 }}>{a.actionTaken}</code>
                          </div>
                        )}
                      </div>
                      <button onClick={() => deleteAlert(a.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 16, marginLeft: 12 }}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
